/**
 * useResourceCatalog Hook
 * Manages ResourceCatalog data and operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchResourceCatalogs,
  fetchResourceCatalog,
  fetchResourceCatalogsByType,
  fetchResourceCatalogsByStatus,
  fetchResourceCatalogByCode,
  createResourceCatalog,
  updateResourceCatalog,
  deleteResourceCatalog,
  validateResourceCodeUnique,
} from '../services/resourceService';

/**
 * Custom hook for managing resource catalog data and operations
 * @param {Object} options - Hook options
 * @param {Object} options.filter - Optional filter criteria
 * @param {string} options.resourceTypeId - Filter by resource type ID
 * @param {string} options.status - Filter by status (ACTIVE/INACTIVE)
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Resource catalog data, mutations, loading states, and utility functions
 */
export function useResourceCatalog(options = {}) {
  const { filter = null, resourceTypeId = null, status = null, enabled = true } = options;
  const queryClient = useQueryClient();

  // Determine which fetch function to use based on options
  const fetchFunction = () => {
    if (resourceTypeId) {
      return fetchResourceCatalogsByType(resourceTypeId, status);
    }
    if (status) {
      return fetchResourceCatalogsByStatus(status);
    }
    return fetchResourceCatalogs(filter);
  };

  // Fetch resource catalog entries
  const {
    data: catalogEntries = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['resourceCatalog', filter, resourceTypeId, status],
    queryFn: fetchFunction,
    enabled,
  });

  // Create resource catalog entry mutation
  const createMutation = useMutation({
    mutationFn: createResourceCatalog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resourceCatalog'] });
    },
  });

  // Update resource catalog entry mutation
  const updateMutation = useMutation({
    mutationFn: updateResourceCatalog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resourceCatalog'] });
    },
  });

  // Delete resource catalog entry mutation
  const deleteMutation = useMutation({
    mutationFn: deleteResourceCatalog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resourceCatalog'] });
    },
  });

  // Validate resource code uniqueness mutation
  const validateCodeMutation = useMutation({
    mutationFn: ({ resourceCode, excludeId }) =>
      validateResourceCodeUnique(resourceCode, excludeId),
  });

  // Utility functions
  const getActiveResources = () => catalogEntries.filter((entry) => entry.status === 'ACTIVE');

  const getInactiveResources = () => catalogEntries.filter((entry) => entry.status === 'INACTIVE');

  const getResourcesByType = (typeId) =>
    catalogEntries.filter((entry) => entry.resourceTypeId === typeId);

  const getResourcesByDomain = (domain) =>
    catalogEntries.filter((entry) => entry.domain === domain);

  const searchResources = (searchTerm) => {
    if (!searchTerm) return catalogEntries;
    const searchLower = searchTerm.toLowerCase();
    return catalogEntries.filter((entry) =>
      entry.resourceCode?.toLowerCase().includes(searchLower) ||
      entry.name?.toLowerCase().includes(searchLower) ||
      entry.description?.toLowerCase().includes(searchLower)
    );
  };

  const getResourceByCode = async (resourceCode) => {
    return fetchResourceCatalogByCode(resourceCode);
  };

  return {
    // Data
    catalogEntries,
    isLoading,
    error,

    // Mutations
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    update: updateMutation.mutate,
    updateAsync: updateMutation.mutateAsync,
    remove: deleteMutation.mutate,
    removeAsync: deleteMutation.mutateAsync,
    validateCode: validateCodeMutation.mutate,
    validateCodeAsync: validateCodeMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isValidating: validateCodeMutation.isPending,
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    validateError: validateCodeMutation.error,

    // Utility functions
    refetch,
    getActiveResources,
    getInactiveResources,
    getResourcesByType,
    getResourcesByDomain,
    searchResources,
    getResourceByCode,
  };
}

/**
 * Custom hook for fetching a single resource catalog entry
 * @param {string} id - Resource catalog ID
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {Object} Resource catalog data and loading state
 */
export function useResourceCatalogEntry(id, options = {}) {
  const { enabled = true } = options;

  const { data: catalogEntry, isLoading, error } = useQuery({
    queryKey: ['resourceCatalogEntry', id],
    queryFn: () => fetchResourceCatalog(id),
    enabled: enabled && !!id,
  });

  return {
    catalogEntry,
    isLoading,
    error,
  };
}

/**
 * Custom hook for fetching resources by type with real-time updates
 * @param {string} resourceTypeId - Resource type ID
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @param {string} options.status - Optional status filter
 * @returns {Object} Resource catalog data filtered by type
 */
export function useResourceCatalogByType(resourceTypeId, options = {}) {
  const { enabled = true, status = null } = options;

  const { data: catalogEntries = [], isLoading, error, refetch } = useQuery({
    queryKey: ['resourceCatalogByType', resourceTypeId, status],
    queryFn: () => fetchResourceCatalogsByType(resourceTypeId, status),
    enabled: enabled && !!resourceTypeId,
  });

  return {
    catalogEntries,
    isLoading,
    error,
    refetch,
  };
}
